/**
 * SeedFromAdModal — Decoupled Mode/Framing seed controls.
 *
 * Two independent axes drive how the seed creative is used:
 *
 *   Strategy Mode  — how much of the hook + angle to inherit
 *     • Same Strategy      — pin seed's hook + angle; iterate the formula
 *     • Explore New Angles — inherit visual style/brand voice; free angles
 *
 *   Visual Framing — how faithfully the seed image is mirrored in generation
 *     • None        — metadata-only; seed image not passed to image gen
 *     • Style Only  — inherit palette, lighting, mood
 *     • Composition — match layout, focal placement, text zones  ← default
 *     • Strict      — clone the exact structural template
 *
 * Selecting a Strategy Mode auto-suggests a compatible Framing (users can
 * override). Unusual combinations show an inline hint.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExternalLink,
  Image as ImageIcon,
  Info,
  Layers,
  Loader2,
  Palette,
  RefreshCw,
  Sparkles,
  Video,
  X,
} from 'lucide-react';
import {
  adGenerationApi,
  type ConnectedAd,
  type GenerationBrief,
} from '@/services/api';

interface Props {
  ad: ConnectedAd;
  brief: GenerationBrief;
  onClose: () => void;
}

type StrategyMode = 'same_strategy' | 'explore_new_angles';
type VisualFraming = 'none' | 'style_match' | 'composition_match' | 'strict_template';

// ── Strategy Mode metadata ──────────────────────────────────────────────────

const STRATEGY_META: Record<
  StrategyMode,
  {
    label: string;
    pitch: string;
    body: string;
    suggestedFraming: VisualFraming;
    icon: typeof Sparkles;
  }
> = {
  same_strategy: {
    label: 'Same Strategy',
    pitch: "Pin the seed's hook + angle; iterate the formula",
    body:
      'Constrains hook type and angle archetype to match the seed ad. ' +
      'Use when an ad is already performing and you want variations that stay true to the winning formula.',
    suggestedFraming: 'composition_match',
    icon: Sparkles,
  },
  explore_new_angles: {
    label: 'Explore New Angles',
    pitch: 'Inherit visual style + brand voice; generate distinct angles',
    body:
      'Keeps visual style and brand voice from the seed, but the pipeline freely generates ' +
      'new hook/angle combinations. Best for scaling reach — gives the targeting engine more angles to test.',
    suggestedFraming: 'style_match',
    icon: RefreshCw,
  },
};

// ── Visual Framing metadata ──────────────────────────────────────────────────

const FRAMING_META: Record<
  VisualFraming,
  {
    label: string;
    hint: string;
    icon: typeof Palette;
  }
> = {
  none: {
    label: 'None',
    hint: 'Metadata only — seed image not passed to image generation',
    icon: Info,
  },
  style_match: {
    label: 'Style Only',
    hint: 'Inherit palette, lighting, and mood',
    icon: Palette,
  },
  composition_match: {
    label: 'Composition',
    hint: 'Match layout, focal placement, and text zones',
    icon: Layers,
  },
  strict_template: {
    label: 'Strict',
    hint: 'Clone the exact structural template of the seed',
    icon: Sparkles,
  },
};

// ── Combo hints (shown when an unusual mode+framing pair is selected) ────────

function getComboHint(mode: StrategyMode | null, framing: VisualFraming): string | null {
  if (!mode) return null;
  if (mode === 'same_strategy' && framing === 'none') {
    return "You're pinning the strategy (hook + angle) but not using the seed image visually. " +
      'The pipeline will iterate the formula with no visual anchor — fine if you want textual continuity only.';
  }
  if (mode === 'same_strategy' && framing === 'strict_template') {
    return 'Strict + Same Strategy will clone the structural template and lock the hook/angle. ' +
      'This is the closest to "replicate the winning ad" — great for validated creative, lower creative diversity.';
  }
  if (mode === 'explore_new_angles' && framing === 'strict_template') {
    return 'Strict visual template + new angles is the most powerful combo: the layout stays identical ' +
      'but the hook/angle explore new territory. Ideal for layout-locked scaling.';
  }
  return null;
}

export function SeedFromAdModal({ ad, brief, onClose }: Props) {
  const navigate = useNavigate();
  const [strategyMode, setStrategyMode] = useState<StrategyMode | null>(null);
  const [framing, setFraming] = useState<VisualFraming>('composition_match');
  const [framingUserOverride, setFramingUserOverride] = useState(false);
  const [totalAds, setTotalAds] = useState<number>(4);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Auto-suggest framing when strategy changes, unless user already overrode it
  function selectStrategy(m: StrategyMode) {
    setStrategyMode(m);
    if (!framingUserOverride) {
      setFraming(STRATEGY_META[m].suggestedFraming);
    }
  }

  function selectFraming(f: VisualFraming) {
    setFraming(f);
    setFramingUserOverride(true);
  }

  const isVideo = ad.media_type === 'video';
  const canSubmit = strategyMode !== null && !submitting;
  const comboHint = strategyMode ? getComboHint(strategyMode, framing) : null;

  async function handleSubmit() {
    if (!strategyMode) return;
    setSubmitting(true);
    setError(null);
    try {
      await adGenerationApi.submitJob({
        client_uid: brief.client_uid,
        brief_id: brief.brief_id,
        total_ads: totalAds,
        seed_creative_id: ad.creative_id,
        seed_mode: strategyMode,
        seed_framing: framing,
        live_view_enabled: true,
      });
      // Drop the user straight onto the brief's Assets tab so they can see
      // the new generation run appear in context.
      navigate(`/creative/briefs/${brief.brief_id}?lens=assets`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit job');
    } finally {
      setSubmitting(false);
    }
  }

  const strategyCls = (m: StrategyMode) =>
    strategyMode === m
      ? 'border-brand-purple bg-brand-purple/5 ring-2 ring-brand-purple/20'
      : 'border-slate-300 hover:border-brand-purple/40';

  const framingCls = (f: VisualFraming) =>
    framing === f
      ? 'border-brand-purple bg-brand-purple/5 ring-1 ring-brand-purple/20'
      : 'border-slate-200 hover:border-slate-300';

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between z-10">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              New asset pack from winning ad
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Brief: <span className="text-slate-700">{brief.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Seed ad summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex gap-3">
            <div className="flex-shrink-0 w-16 h-16 bg-white border border-slate-200 rounded overflow-hidden flex items-center justify-center">
              {ad.thumbnail_url ? (
                <img
                  src={ad.thumbnail_url}
                  alt={ad.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : isVideo ? (
                <Video size={18} className="text-slate-400" />
              ) : (
                <ImageIcon size={18} className="text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-900 truncate">
                  {ad.name || ad.creative_id}
                </span>
                {ad.source_url && (
                  <a
                    href={ad.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                    title="Open source"
                  >
                    <ExternalLink size={11} />
                  </a>
                )}
              </div>
              <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                {ad.creative_id}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-slate-600">
                {ad.hook_type && (
                  <span><span className="text-slate-400">hook:</span> {ad.hook_type}</span>
                )}
                {ad.angle_archetype && (
                  <span><span className="text-slate-400">angle:</span> {ad.angle_archetype}</span>
                )}
                {ad.visual_style && (
                  <span><span className="text-slate-400">visual:</span> {ad.visual_style}</span>
                )}
                {ad.primary_emotion && (
                  <span><span className="text-slate-400">emotion:</span> {ad.primary_emotion}</span>
                )}
              </div>
            </div>
          </div>

          <>
              {/* ── Section 1: Strategy Mode ── */}
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                  Strategy Mode
                </div>
                <p className="text-[11px] text-slate-500 mb-2.5">
                  How much of the seed's hook + angle to inherit
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {(['same_strategy', 'explore_new_angles'] as StrategyMode[]).map((m) => {
                    const meta = STRATEGY_META[m];
                    const Icon = meta.icon;
                    const selected = strategyMode === m;
                    return (
                      <button
                        key={m}
                        onClick={() => selectStrategy(m)}
                        className={`text-left border rounded-lg p-3 transition ${strategyCls(m)}`}
                      >
                        <div className="flex items-start gap-2">
                          <Icon
                            size={15}
                            className={selected ? 'text-brand-purple mt-0.5' : 'text-slate-400 mt-0.5'}
                          />
                          <div className="flex-1">
                            <div className={`text-sm font-semibold ${selected ? 'text-brand-purple' : 'text-slate-900'}`}>
                              {meta.label}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {meta.pitch}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 mt-2 leading-relaxed">
                          {meta.body}
                        </div>
                        {selected && (
                          <div className="mt-2 text-[10px] text-brand-purple/70 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-purple/50 inline-block" />
                            Suggests: {FRAMING_META[meta.suggestedFraming].label} framing
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Section 2: Visual Framing ── */}
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                  Visual Framing
                </div>
                <p className="text-[11px] text-slate-500 mb-2.5">
                  How faithfully the seed image is mirrored in generation
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(['none', 'style_match', 'composition_match', 'strict_template'] as VisualFraming[]).map((f) => {
                    const meta = FRAMING_META[f];
                    const Icon = meta.icon;
                    const selected = framing === f;
                    return (
                      <button
                        key={f}
                        onClick={() => selectFraming(f)}
                        className={`text-left border rounded-lg p-2.5 transition ${framingCls(f)}`}
                      >
                        <Icon
                          size={13}
                          className={selected ? 'text-brand-purple mb-1' : 'text-slate-400 mb-1'}
                        />
                        <div className={`text-xs font-semibold ${selected ? 'text-brand-purple' : 'text-slate-800'}`}>
                          {meta.label}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                          {meta.hint}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Combo hint */}
                {comboHint && (
                  <div className="mt-2.5 bg-amber-50 border border-amber-200 rounded p-2.5 flex gap-2">
                    <Info size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-800 leading-relaxed">{comboHint}</p>
                  </div>
                )}
              </div>

              {/* ── Pack size ── */}
              <div className="border-t border-slate-100 pt-4">
                <div className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
                  Pack size
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-slate-600">Total ads</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={totalAds}
                    onChange={(e) =>
                      setTotalAds(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
                    }
                    className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
                  />
                  <span className="text-[11px] text-slate-400">
                    {strategyMode === 'explore_new_angles'
                      ? `→ up to ${totalAds} distinct novel angles`
                      : `→ ${totalAds} variations on the same formula`}
                  </span>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
                  {error}
                </div>
              )}
            </>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-4 py-1.5 text-xs font-semibold rounded transition flex items-center gap-1.5 ${
              canSubmit
                ? 'bg-brand-purple text-white hover:bg-brand-purple/90'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {submitting
              ? 'Submitting…'
              : strategyMode
                ? `Generate — ${STRATEGY_META[strategyMode].label} · ${FRAMING_META[framing].label}`
                : 'Choose a strategy mode'}
          </button>
        </div>
      </div>
    </div>
  );
}
